/**
 * CustomDefenseContext
 *
 * Shared source of truth for the current user's saved Custom_Defense records. Both the
 * Simulator (CoverageSelector / AnimationEngine / PlayArt, via useDefenseSelection) and the
 * Defense Builder (CustomDefenseManager) read from this single context, so a save/delete
 * performed in one place is immediately visible everywhere else without a re-fetch (Req 7.3).
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 1.10, 2.4, 6.6
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { usePremiumStatus } from '../hooks';
import { throwOnErrors } from '../utils';
import { classifyCustomDefense } from '../rules';
import { CustomDefense, CustomDefenseAssignments, CustomDefenseLoadState, CoverageType, FormationType } from '../types';

const client = generateClient<Schema>();

/**
 * Raw shape returned by the fetchCustomDefenses query — flat per-position coverage
 * fields (see amplify/data/resource.ts) plus the implicit `owner` field Amplify adds for a
 * model authorized via `allow.owner()`.
 */
interface StoredCustomDefense {
  id: string;
  name: string;
  formation: string;
  owner?: string | null;
  cb1Coverage: string;
  dblb1Coverage: string;
  fsCoverage: string;
  ssCoverage: string;
  dblb2Coverage: string;
  cb2Coverage: string;
  lbCoverage: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Maps the flat per-position backend fields to the Assignments record shape (mirrors the
 *  equivalent mapper in CustomDefenseManager.tsx). */
function fieldsToAssignments(defense: StoredCustomDefense): CustomDefenseAssignments {
  return {
    cb1: defense.cb1Coverage as CoverageType,
    dblb1: defense.dblb1Coverage as CoverageType,
    fs: defense.fsCoverage as CoverageType,
    ss: defense.ssCoverage as CoverageType,
    dblb2: defense.dblb2Coverage as CoverageType,
    cb2: defense.cb2Coverage as CoverageType,
    lb: defense.lbCoverage as CoverageType,
  };
}

function toCustomDefense(defense: StoredCustomDefense): CustomDefense {
  return {
    id: defense.id,
    name: defense.name,
    formation: (defense.formation as FormationType) ?? 'doubles',
    assignments: fieldsToAssignments(defense),
    createdAt: defense.createdAt,
    updatedAt: defense.updatedAt,
  };
}

/**
 * Filters a list of raw records down to only those owned by the requesting user (Req 1.2,
 * 1.3). This is a defensive safety net, not a trust boundary the app relies on — ownership is
 * already enforced server-side by the premium-plays Lambda.
 *
 * Accepts BOTH stored owner formats. Records written by the current Lambda hold the bare Cognito
 * sub; anything created through the old model path holds Amplify's default owner claim,
 * `sub::username`. Comparing against the bare sub alone would silently drop every legacy record
 * *after* the server had correctly returned it — the same mismatch that made saved plays
 * invisible. See the `caller` function in amplify/functions/premium-plays/handler.ts.
 */
export function filterOwnedDefenses(records: StoredCustomDefense[], userId: string | null): StoredCustomDefense[] {
  if (!userId) return [];
  return records.filter(
    (record) => record.owner === userId || record.owner?.startsWith(`${userId}::`)
  );
}

export interface CustomDefenseContextValue {
  loadState: CustomDefenseLoadState;
  /** All of the current user's defenses, owner-filtered, draft and complete alike. */
  defenses: CustomDefense[];
  /** Derived via classifyCustomDefense on every render — only these are selectable in the Simulator. */
  completeDefenses: CustomDefense[];
  refetch: () => Promise<void>;
  /** Called by delete flows so every consumer sees the removal immediately (Req 7.3). */
  removeDefenseLocally: (id: string) => void;
  /** Called by save flows so the Simulator sees a newly-saved defense without a full refetch. */
  addOrUpdateDefenseLocally: (defense: CustomDefense) => void;
}

const CustomDefenseContext = createContext<CustomDefenseContextValue | undefined>(undefined);

interface CustomDefenseProviderProps {
  children: ReactNode;
}

export function CustomDefenseProvider({ children }: CustomDefenseProviderProps) {
  const { isPremium, isAuthenticated, userId } = usePremiumStatus();
  const [loadState, setLoadState] = useState<CustomDefenseLoadState>({ status: 'not_requested' });
  const [defenses, setDefenses] = useState<CustomDefense[]>([]);
  // Tracks whether (isPremium && isAuthenticated) was true on the previous render, so we can
  // detect a false -> true flip (e.g. the initial premium/auth check resolving, or a mid-session
  // login) and fetch exactly then — never on mount when not gated (Req 1.9, 1.10).
  const wasGatedRef = useRef(false);

  const fetchDefenses = useCallback(async () => {
    setLoadState({ status: 'loading' });
    try {
      // Goes through the tier-checked Lambda rather than the model, so a lapsed subscriber cannot
      // retrieve their defenses by calling the API directly. The Lambda already returns only the
      // caller's own records; filterOwnedDefenses stays as a client-side belt-and-braces check.
      const { data, errors } = await client.queries.fetchCustomDefenses();
      // The Amplify client resolves with `errors` set instead of throwing, so without this a
      // server-side failure would be indistinguishable from "you have no saved defenses".
      throwOnErrors(errors);

      const owned = filterOwnedDefenses((data || []) as StoredCustomDefense[], userId);
      setDefenses(owned.map(toCustomDefense));
      setLoadState({ status: 'loaded' });
    } catch (err) {
      console.error('Error fetching custom defenses:', err);
      setLoadState({ status: 'error' });
    }
  }, [userId]);

  useEffect(() => {
    const isGated = isPremium && isAuthenticated;
    if (isGated && !wasGatedRef.current) {
      fetchDefenses();
    } else if (!isGated) {
      // Non_Premium_User / Guest_User (or logged out mid-session): no retrieval, no stale data.
      setLoadState({ status: 'not_requested' });
      setDefenses([]);
    }
    wasGatedRef.current = isGated;
  }, [isPremium, isAuthenticated, fetchDefenses]);

  const removeDefenseLocally = useCallback((id: string) => {
    setDefenses((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const addOrUpdateDefenseLocally = useCallback((defense: CustomDefense) => {
    setDefenses((prev) => {
      const existingIndex = prev.findIndex((d) => d.id === defense.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = defense;
        return next;
      }
      return [...prev, defense];
    });
  }, []);

  // Derived fresh on every render — never cached — so a classification-registry update is
  // reflected immediately for existing saved assignments without any re-save (Req 6.6).
  const completeDefenses = defenses.filter((d) => classifyCustomDefense(d.assignments) === 'complete');

  const value: CustomDefenseContextValue = {
    loadState,
    defenses,
    completeDefenses,
    refetch: fetchDefenses,
    removeDefenseLocally,
    addOrUpdateDefenseLocally,
  };

  return <CustomDefenseContext.Provider value={value}>{children}</CustomDefenseContext.Provider>;
}

/**
 * Custom hook to access the custom defense context.
 * Throws an error if used outside of CustomDefenseProvider.
 */
export function useCustomDefenses(): CustomDefenseContextValue {
  const context = useContext(CustomDefenseContext);
  if (context === undefined) {
    throw new Error('useCustomDefenses must be used within a CustomDefenseProvider');
  }
  return context;
}
